import { Box, Collapse } from '@mui/material';
import BackButton from '@acorn-portal/shared/src/components/buttons/BackButton';
import ContinueButton from '@acorn-portal/shared/src/components/buttons/ContinueButton';
import JourneyCardLayout from '@acorn-portal/shared/src/layouts/journey-card-layout';
import DatePickerField from '@acorn-portal/shared/src/components/datePicker/DropDownDatePicker';
import ModificationForm from '@acorn-portal/shared/src/components/forms/ModificationForm';
import RadioGroupButton from '@acorn-portal/shared/src/components/inputs/radio-button-group';
import AutoCompleteFormField from '@acorn-portal/shared/src/components/inputs/AutoCompleteFormField';
import FormTextField from '@acorn-portal/shared/src/components/inputs/FormTextField';
import ActionButtons from '@acorn-portal/shared/src/components/wrapper/action-buttons';
import HelperModal from '@acorn-portal/shared/src/components/modal/HelperModal';
import SelectFormField, { IOptions } from '@acorn-portal/shared/src/components/inputs/select';
import { formatISO, isAfter, subYears } from 'date-fns';
import { Form, Formik, FormikHelpers } from 'formik';
import { useAppDispatch, useAppSelector } from '@acorn-portal/shared/src/hooks/reduxHook';
import {
	handleChangeOfVehicleRating,
	IChangeOfVehiclePart2,
} from '@acorn-portal/shared/src/models/midTermAdjustment-model';
import { useDiscardMta } from '@acorn-portal/shared/src/hooks/use-discard-mta';
import useTrack from '@acorn-portal/shared/src/hooks/useTrack/useTrack';
import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';
import {
	updateChangeOfVehicle,
	updateMidTermAdjustmentDetails,
} from '@acorn-portal/shared/src/store/midTermAdjustment.slice';
import { setMidTermAdjustmentRatingResponse } from '@acorn-portal/shared/src/store/midTermAdjustmentRatingResponse.slice';
import ToggleButtonFormField from '@acorn-portal/shared/src/components/inputs/toggle-button-form-field';
import { VEHICLE_DETAILS_PART_TWO_ERROR_MESSAGES } from '@acorn-portal/shared/src/utils/constants/ErrorMessages';
import {
	COVER_LEVEL,
	COVER_LEVEL_UPDATED,
	TAXI_VEHICLE_USE,
	VEHICLE_LEFT_HAND_DRIVE,
	OVERNIGHT_ADDRESS_DIFFERENT,
} from '@acorn-portal/shared/src/utils/modalText';
import { ROUTE_PATHS } from '@acorn-portal/shared/src/utils/Routes';
import { Product, TelematicDeviceProvider } from '@acorn-portal/shared/src/services/interface/Shared';
import CvAdditionalQuestions from '@acorn-portal/shared/src/components/forms/CvAdditionalQuestions';
import TelematicQuestions from '@acorn-portal/shared/src/components/forms/TelematicQuestions';
import { ESTIMATED_VALUE_MODAL } from '@acorn-portal/shared/src/utils/modalText';
import { isNotInFutureMonth } from '@acorn-portal/shared/src/utils';
import ChangeOfVehiclePart2Schema from '@acorn-portal/shared/src/utils/validation/ChangeOfVehiclePart2Schema';
import AddressLookup from '@acorn-portal/shared/src/components/addressLookup';
import { useVehicleUsageOptions } from '@acorn-portal/shared/src/utils/taxiVehicleUsageOptions';
import ScrollToError from '@acorn-portal/shared/src/components/scroll-to-error';
import { Address } from '../../services/api';
import hasOverNightAddress from '../../utils/has-overnight-address';
import { FormikLogErrors } from '@acorn-portal/shared/src/pages/mta/components/FormikLogErrors';

type LocationProps = {
	state: {
		registration: string;
		yearOfManufacture: number;
		bodyType: string;
	};
};

const ChangeOfVehiclePart2 = () => {
	const [open, toggleModal] = useState(false);
	const state = useAppSelector((redux) => redux.midTermAdjustment);
	const vehicleLookup = useAppSelector((redux) => redux.vehicleLookup);
	const lists = useAppSelector((redux) => redux.referenceData);
	const location = useLocation() as unknown as LocationProps;
	const { discardLoading } = useDiscardMta();
	const dispatch = useAppDispatch();
	const [loading, setLoading] = useState(false);
	const navigate = useNavigate();
	const fullPolicy = useAppSelector((redux) => redux.fullPolicy);
	const isTaxi = fullPolicy.product === Product.TAXI;
	const { vehicleInfo } = vehicleLookup;
	const isMyPolicy = fullPolicy.product === 'TELEMATICS' && import.meta.env.VITE_AUTH_REALM === 'MyPolicy';
	const { track } = useTrack();

	const vehicleUsageOptions = useVehicleUsageOptions();

	useEffect(() => {
		track(`Vehicle Details 2 Page Viewed`);
	}, []);

	useEffect(() => {
		if (!state.policyReference) {
			navigate(ROUTE_PATHS.home);
		}
		if (state.vehicleDetails.registration !== fullPolicy.vehicleDetails.registration && state.transactionId) {
			navigate(ROUTE_PATHS.makeAChangeSummary);
		}
	}, [location.state]);

	const initialValues: IChangeOfVehiclePart2 = {
		vehicleUse: '',
		registeredKeeper: '',
		owner: '',
		hasModifications: '',
		imported: '',
		value: '',
		levelOfCover: isMyPolicy ? 'COMP' : '',
		purchaseDate: { year: '', month: '' },
		countryOfRegistration: '',
		modifications: [],
		isLeftHandDrive: '',
		businessRadius: '',
		hasInternalRacking: '',
		vehicleCarriesDangerousGoods: '',
		vehicleVisitsHazardousSites: '',
		telematicsBox: '',
		keptOvernight: state.vehicleDetails.vehicleOvernight || '',
		overnightAddress: {
			...(state.vehicleDetails.overnightAddress || {
				line1: '',
				city: '',
				postcode: '',
			}),
		},
		overnightAddressIsAway: hasOverNightAddress(
			state.policyDetails.policyholder.address,
			state.vehicleDetails.overnightAddress ?? ({} as Address),
		)
			? 'yes'
			: 'no',
	};

	const handleOnSubmit = async (values: IChangeOfVehiclePart2, formikHelpers: FormikHelpers<IChangeOfVehiclePart2>) => {
		const datePurchased = new Date(
			parseInt(values.purchaseDate.year, 10),
			parseInt(values.purchaseDate.month, 10) - 1,
			1,
		);
		const manufactureDate = new Date(vehicleLookup.vehicleInfo.year as number, 0, 1);
		if (isAfter(manufactureDate, datePurchased)) {
			formikHelpers.setErrors({
				purchaseDate: {
					year: `${VEHICLE_DETAILS_PART_TWO_ERROR_MESSAGES.purchaseMinDate} (${manufactureDate.getFullYear()})`,
				},
			});
			return;
		}

		if (!isNotInFutureMonth(datePurchased)) {
			formikHelpers.setErrors({
				purchaseDate: {
					month: `${VEHICLE_DETAILS_PART_TWO_ERROR_MESSAGES.purchaseMaxDate}`,
				},
			});
			return;
		}

		try {
			setLoading(true);

			if (fullPolicy.product === Product.TAXI) {
				values.operatingArea = state.policyDetails.operatingArea ?? fullPolicy.coverDetails.operatingArea;
			} else {
				values.vehicleUse = state.vehicleDetails.vehicleUse ?? '';
				values.operatingArea = '';
			}

			const updatedState = {
				...state,
				vehicleDetails: {
					...state.vehicleDetails,
					...vehicleInfo,
					isLeftHandDrive: values.isLeftHandDrive === 'yes',
					vehicleOvernight: values.keptOvernight,
					overnightAddress:
						values.overnightAddressIsAway === 'yes'
							? values.overnightAddress
							: state.policyDetails.policyholder.address,
					registration: location.state.registration,
					yearOfManufacture: location.state.yearOfManufacture,
					vehicleUse: values.vehicleUse ?? '',
					bodyType: location.state.bodyType ?? vehicleInfo.bodyType?.code ?? '',
					numberOfDoors: vehicleInfo.numberOfDoors?.code ?? '',
					registeredKeeper: values.registeredKeeper,
					abiCode: vehicleInfo.abiCode ?? '',
					engineSize: vehicleInfo.cubicCapacity ?? 0,
					abiGroup: parseInt(vehicleInfo.abiGroup ?? '0', 10),
					fuelType: vehicleInfo.fuelType?.code ?? '',
					transmission: vehicleInfo.transmission?.code ?? '',
					ownership: values.owner,
					isImported: values.imported === 'yes',
					value: parseInt(values.value, 10),
					make: vehicleInfo.make?.code ?? '',
					model: vehicleInfo.model?.code ?? '',
					coverDefinitionCode: values.levelOfCover,
					modifications: values.hasModifications === 'yes' ? values.modifications : [],
					datePurchased: formatISO(datePurchased),
					countryOfRegistration: values.countryOfRegistration,
					telematicsBox: values.telematicsBox === 'yes',
					grossWeight: parseInt(vehicleInfo.grossWeight ?? '0', 10),
				},
			};

			dispatch(updateMidTermAdjustmentDetails(updatedState));

			const result = await handleChangeOfVehicleRating({
				midTermAdjustment: updatedState,
				vehicleLookupResponse: vehicleLookup,
				values: {
					...values,
					modifications: values.hasModifications === 'yes' ? values.modifications : [],
				},
				product: fullPolicy.product,
			});

			if (!result) throw new Error('No result from handleChangeOfVehicleRating');

			if (result.hasDeclined) {
				navigate(ROUTE_PATHS.makeAChangeReferral);
				return;
			}

			dispatch(setMidTermAdjustmentRatingResponse(result));
			dispatch(updateChangeOfVehicle(true));
			setLoading(false);
			navigate(ROUTE_PATHS.makeAChangeSummary);
		} catch (error) {
			console.error(error);
			setLoading(false);
			navigate(ROUTE_PATHS.error);
		}
	};

	const deviceProvider = fullPolicy.coverDetails.deviceProvider;
	const hasSelfFitDevice =
		deviceProvider !== undefined &&
		[TelematicDeviceProvider.THINGCO, TelematicDeviceProvider.TRAKM8].includes(
			deviceProvider as TelematicDeviceProvider,
		);

	return (
		<JourneyCardLayout pageTitle='New Vehicle Details' loading={loading || discardLoading}>
			<Formik
				key={JSON.stringify(state.transactionId)}
				initialValues={initialValues}
				onSubmit={handleOnSubmit}
				validationSchema={ChangeOfVehiclePart2Schema({
					state,
					fullPolicy,
				})}
			>
				{(formik) => {
					return (
						<Form>
							<FormikLogErrors />
							<ScrollToError>
								{isTaxi && (
									<ToggleButtonFormField
										label='What will you be using the vehicle for?'
										name='vehicleUse'
										id='vehicleUse'
										value={formik.values.vehicleUse}
										onChange={(value) => {
											formik.setFieldValue('vehicleUse', value);
										}}
										options={vehicleUsageOptions}
										error={formik.touched.vehicleUse && Boolean(formik.errors.vehicleUse)}
										helperText={formik.touched.vehicleUse && formik.errors.vehicleUse}
										modal={{
											title: TAXI_VEHICLE_USE.header,
											description: TAXI_VEHICLE_USE.text,
										}}
										product={fullPolicy.product}
									/>
								)}
								<AutoCompleteFormField
									label={
										isTaxi
											? 'From the date this change takes effect, who will be the owner and registered keeper of this vehicle?'
											: 'Who currently owns the vehicle?'
									}
									name='owner'
									id='owner'
									value={formik.values.owner}
									onChange={(value) => formik.setFieldValue('owner', value)}
									options={lists.vehicleOwnershipOptions}
									error={formik.touched.owner && Boolean(formik.errors.owner)}
									helperText={formik.touched.owner && formik.errors.owner}
								/>
								<AutoCompleteFormField
									label='Who is the registered keeper of the vehicle?'
									name='registeredKeeper'
									id='registeredKeeper'
									value={formik.values.registeredKeeper}
									onChange={(value) => formik.setFieldValue('registeredKeeper', value)}
									options={lists.vehicleKeeperOptions}
									error={formik.touched.registeredKeeper && Boolean(formik.errors.registeredKeeper)}
									helperText={formik.touched.registeredKeeper && formik.errors.registeredKeeper}
								/>
								<RadioGroupButton
									label='Has the vehicle been modified?'
									name='hasModifications'
									id='hasModifications'
									value={formik.values.hasModifications}
									onChange={(value) => formik.setFieldValue('hasModifications', value)}
									error={
										(formik.touched.hasModifications && Boolean(formik.errors.hasModifications)) ||
										(formik.touched.hasModifications && Boolean(formik.errors.modifications))
									}
									helperText={
										(formik.touched.hasModifications && formik.errors.hasModifications) ||
										(formik.touched.hasModifications && (formik.errors.modifications as string))
									}
								/>
								<Collapse in={formik.values.hasModifications === 'yes'}>
									<ModificationForm
										hasGlobalError={formik.touched.hasModifications && Boolean(formik.errors.modifications)}
										onChange={(value) => formik.setFieldValue('modifications', value)}
										value={formik.values.modifications}
									/>
								</Collapse>
								<RadioGroupButton
									label='Is the vehicle imported?'
									name='imported'
									id='imported'
									value={formik.values.imported}
									onChange={(value) => {
										formik.setFieldValue('imported', value);
										if (value === 'no') {
											formik.setFieldValue('countryOfRegistration', '', false);
										}
									}}
									error={formik.touched.imported && Boolean(formik.errors.imported)}
									helperText={formik.touched.imported && formik.errors.imported}
								/>
								<Collapse in={formik.values.imported === 'yes'}>
									<Box>
										<AutoCompleteFormField
											label='Initial Country of Registration'
											name='countryOfRegistration'
											id='countryOfRegistration'
											value={formik.values.countryOfRegistration}
											onChange={(value) => formik.setFieldValue('countryOfRegistration', value)}
											options={lists.countryEarnedNcdOptions}
											error={formik.touched.countryOfRegistration && Boolean(formik.errors.countryOfRegistration)}
											helperText={formik.touched.countryOfRegistration && formik.errors.countryOfRegistration}
										/>
									</Box>
								</Collapse>
								<RadioGroupButton
									label='Is the vehicle left-hand drive?'
									name='isLeftHandDrive'
									id='isLeftHandDrive'
									value={formik.values.isLeftHandDrive}
									onChange={(value) => formik.setFieldValue('isLeftHandDrive', value)}
									error={formik.touched.isLeftHandDrive && Boolean(formik.errors.isLeftHandDrive)}
									helperText={formik.touched.isLeftHandDrive && formik.errors.isLeftHandDrive}
									modal={{
										title: VEHICLE_LEFT_HAND_DRIVE.header,
										description: VEHICLE_LEFT_HAND_DRIVE.text,
									}}
								/>
								<DatePickerField
									id='purchaseDate'
									onChange={(value) => formik.setFieldValue('purchaseDate', value)}
									label='Purchased Date'
									value={formik.values.purchaseDate}
									error={
										(formik.touched.purchaseDate?.month && Boolean(formik.errors.purchaseDate?.month)) ||
										(formik.touched.purchaseDate?.year && Boolean(formik.errors.purchaseDate?.year))
									}
									helperText={
										(formik.touched.purchaseDate?.month && formik.errors.purchaseDate?.month) ||
										(formik.touched.purchaseDate?.year && formik.errors.purchaseDate?.year)
									}
									day={false}
									minYear={subYears(new Date(), 25).getFullYear()}
								/>
								<ToggleButtonFormField
									label='Where is the vehicle kept overnight?'
									name='keptOvernight'
									id='keptOvernight'
									value={formik.values.keptOvernight}
									onChange={(value) => formik.setFieldValue('keptOvernight', value)}
									options={lists.vehicleStorageOptions as IOptions[]}
									error={formik.touched.keptOvernight && Boolean(formik.errors.keptOvernight)}
									helperText={formik.touched.keptOvernight && formik.errors.keptOvernight}
								/>
								<RadioGroupButton
									label='Is your home address different to the address where your vehicle is kept overnight?'
									name='overnightAddressIsAway'
									id='overnightAddressIsAway'
									value={formik.values.overnightAddressIsAway}
									onChange={(value) => {
										formik.setFieldValue('overnightAddressIsAway', value);
									}}
									error={formik.touched.overnightAddressIsAway && Boolean(formik.errors.overnightAddressIsAway)}
									helperText={formik.touched.overnightAddressIsAway && formik.errors.overnightAddressIsAway}
									modal={{
										title: OVERNIGHT_ADDRESS_DIFFERENT.title,
										description: OVERNIGHT_ADDRESS_DIFFERENT.description,
									}}
								/>
								<Collapse in={formik.values.overnightAddressIsAway === 'yes'}>
									<AddressLookup
										initialValue={formik.values.overnightAddress}
										firstFieldLabel='Overnight House number or Name'
										postcodeLabel='Overnight Postcode'
										onChange={(value) => {
											formik.setFormikState((formState) => ({
												...formState,
												values: {
													...formState.values,
													overnightAddress: {
														...formState.values.overnightAddress,
														line1: value?.line1 ?? '',
														line2: value?.line2,
														line3: value?.line3,
														line4: value?.line4,
														city: value?.city ?? '',
														county: value?.county ?? '',
														country: value?.country ?? '',
														postcode: value?.postcode ?? '',
													},
												},
											}));
										}}
									/>
								</Collapse>
								<FormTextField
									id='value'
									name='value'
									label='Estimated Value (£)'
									error={formik.touched.value && Boolean(formik.errors.value)}
									helperText={formik.touched.value && formik.errors.value}
									onChange={formik.handleChange}
									value={formik.values.value}
									format='currency'
									onBlur={formik.handleBlur}
									modal={{
										title: ESTIMATED_VALUE_MODAL.header,
										description: ESTIMATED_VALUE_MODAL.text,
									}}
								/>
								{!isMyPolicy && (
									<SelectFormField
										label='Cover required'
										name='levelOfCover'
										id='levelOfCover'
										value={formik.values.levelOfCover}
										onChange={(value) => formik.setFieldValue('levelOfCover', value)}
										options={
											isTaxi
												? (lists.levelOfCoverTypes as IOptions[])
												: (lists.levelOfCoverTypes.filter((x) => x.value !== 'TPO') as IOptions[])
										}
										error={formik.touched.levelOfCover && Boolean(formik.errors.levelOfCover)}
										helperText={formik.touched.levelOfCover && formik.errors.levelOfCover}
										modal={{
											title: (isTaxi ? COVER_LEVEL_UPDATED : COVER_LEVEL)(fullPolicy.startDate, fullPolicy.product)
												.header,
											description: (isTaxi ? COVER_LEVEL_UPDATED : COVER_LEVEL)(
												fullPolicy.startDate,
												fullPolicy.product,
											).text,
										}}
									/>
								)}
								{hasSelfFitDevice && <TelematicQuestions formik={formik} />}

								{fullPolicy.product === Product.COMMERCIAL_VEHICLE && <CvAdditionalQuestions formik={formik} />}
								<ActionButtons>
									<BackButton onClick={() => toggleModal(true)}>Back</BackButton>
									<ContinueButton type='submit'>Continue</ContinueButton>
								</ActionButtons>
							</ScrollToError>
						</Form>
					);
				}}
			</Formik>
			<HelperModal
				open={open}
				onClose={() => {
					toggleModal(false);
				}}
				title='Discard The Changes?'
				description='Are you sure you wish to discard the changes?'
				closeText='No'
				submitText='Yes'
				onSubmit={() => navigate(-1)}
			/>
		</JourneyCardLayout>
	);
};

export default ChangeOfVehiclePart2;
